import { useMutation } from "@apollo/client/react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { GetRecordComposerDefinitionDetailQuery } from "~/common/gqls";
import type { MutationRefetchQueries } from "~/common/types";
import {
  UpsertEntityComposerDefinitionDocument,
  type UpsertEntityComposerDefinitionMutation,
  type UpsertEntityComposerDefinitionMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { PropertySchemaSetFormData } from "~/features/dataImportProjects/types";
import { PropertySchemasMapper } from "~/features/dataImportProjects/utils/propertySchemasMapper";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { useDefinitionJsonManager } from "./useDefinitionJsonManager";

export function useEntityComposerDefinitionForm(onSubmit: () => void, refetchQueriesOnEdit: MutationRefetchQueries) {
  const [upsertEntityComposerDef] = useMutation<
    UpsertEntityComposerDefinitionMutation,
    UpsertEntityComposerDefinitionMutationVariables
  >(UpsertEntityComposerDefinitionDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });
  const { t } = useTranslation();
  const [processing, setProcessing] = React.useState(false);
  const { tenantId } = useTenant();
  const [name, setName] = React.useState("");

  const jsonManager = useDefinitionJsonManager({ serverDefinition: undefined });
  const { initFromServer, workingJson } = jsonManager;

  const resetByEntityComposerDefinition = useCallback(
    (data: GetRecordComposerDefinitionDetailQuery) => {
      const composerDef = data?.recordComposerDefinitions?.[0];
      if (!composerDef) {
        return;
      }

      initFromServer(composerDef.definition as Record<string, unknown>);
      setName(composerDef.name);
    },
    [initFromServer],
  );

  const upsertEntityComposerDefinition = useCallback(
    async (
      importProjectId: string,
      selectedEvidenceSchemaSet: PropertySchemaSetFormData | undefined,
      evidenceSetIds: string[],
      composerDefId?: string,
      propertySchemaSetId?: string,
    ) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return false;
      }

      const input: UpsertEntityComposerDefinitionMutationVariables["input"] = {
        tenantId,
        id: composerDefId,
        importProjectId,
        name,
        definition: workingJson,
        evidenceSetIds,
        propertySchemaSet: {
          id: propertySchemaSetId,
          schemas:
            selectedEvidenceSchemaSet?.propertySchemaAssociations.map((psa) =>
              PropertySchemasMapper.propertySchemaAssociation.copyWithoutId(psa),
            ) ?? [],
        },
      };

      setProcessing(true);
      try {
        const res = await upsertEntityComposerDef({ variables: { input } });
        if (res.data?.cnutUpsertEntityComposerDefinition.__typename === "ValidationError") {
          const e = res.data.cnutUpsertEntityComposerDefinition;
          toast.error(`${e.code}: ${e.message}`);
          return false;
        }
        toast.success(t("feature.common.createdSuccessfully", { model: "entityComposer" }));
        onSubmit();
        return true;
      } finally {
        setProcessing(false);
      }
    },
    [t, setProcessing, upsertEntityComposerDef, onSubmit, tenantId, name, workingJson],
  );

  return {
    name,
    setName,
    resetByEntityComposerDefinition,
    upsertEntityComposerDefinition,
    processing,
    jsonManager,
  };
}
